// Edge-compatible GraphQL client
import { useBooking } from "@/contexts/BookingContext";
import {
  checkoutErrorMessage,
  isStorefrontDisabledError,
  payloadHasStorefrontDisabledError,
  wait,
} from "@/lib/storefrontErrors";
import { useState, useEffect, useCallback, useRef } from "react";

const API_URL =
  import.meta.env.VITE_APOLLO_CLIENT_URI ||
  "https://api-staging.withsplice.com/graphql";
const STOREFRONT_RETRY_ATTEMPTS = 3;

export interface GraphQLError {
  message: string;
  extensions?: any;
  locations?: Array<{
    line: number;
    column: number;
  }>;
  path?: Array<string | number>;
}

export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: GraphQLError[];
}

function shouldRetryStorefrontResponse<T>(response: GraphQLResponse<T>) {
  if (response.errors?.some((error) => isStorefrontDisabledError(error.message))) {
    return true;
  }
  return payloadHasStorefrontDisabledError(response.data);
}

async function graphqlFetchOnce<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: RequestInit
): Promise<GraphQLResponse<T>> {
  try {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("customer-token")
        : null;

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        query,
        variables,
      }),
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("GraphQL HTTP Error:", errorText);

      // Handle authentication errors
      if (response.status === 401) {
        console.warn("Authentication error detected, clearing token");
        if (typeof window !== "undefined") {
          localStorage.removeItem("customer-token");
        }
      }

      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const json = await response.json();

    if (json.errors && json.errors.length > 0) {
      console.error("GraphQL Errors:", json.errors);

      // Handle authentication errors in GraphQL response
      const authError = json.errors.find(
        (error: any) =>
          error.extensions?.code === "UNAUTHENTICATED" ||
          error.message.includes("Unauthorized")
      );

      if (authError) {
        console.warn("GraphQL authentication error detected, clearing token");
        if (typeof window !== "undefined") {
          localStorage.removeItem("customer-token");
        }
      }
    }

    return json;
  } catch (error) {
    console.error("GraphQL fetch error:", error);
    throw error;
  }
}

// Core fetch function for GraphQL requests
export async function graphqlFetch<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: RequestInit
): Promise<GraphQLResponse<T>> {
  let last: GraphQLResponse<T> | undefined;
  for (let attempt = 0; attempt < STOREFRONT_RETRY_ATTEMPTS; attempt++) {
    last = await graphqlFetchOnce<T>(query, variables, options);
    if (!shouldRetryStorefrontResponse(last) || attempt === STOREFRONT_RETRY_ATTEMPTS - 1) {
      return last;
    }
    await wait(350 * (attempt + 1));
  }
  return last!;
}

// Hook for GraphQL queries
export function useQuery<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: {
    skip?: boolean;
    fetchPolicy?: "cache-first" | "network-only";
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(!options?.skip);
  const [error, setError] = useState<Error | undefined>(undefined);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (options?.skip) {
      setData(undefined);
      setLoading(false);
      return;
    }

    setData(undefined);
    setLoading(true);
    setError(undefined);

    try {
      const response = await graphqlFetch<T>(query, variables);

      const storefrontOnlyError =
        Boolean(response.errors?.length) &&
        response.errors!.every((item) => isStorefrontDisabledError(item.message));

      if (response.data && (!response.errors?.length || storefrontOnlyError)) {
        if (requestId === requestIdRef.current) {
          setData(response.data);
          options?.onCompleted?.(response.data);
        }
      } else if (response.errors && response.errors.length > 0) {
        const error = new Error(response.errors[0].message);
        if (requestId === requestIdRef.current) {
          setError(error);
          options?.onError?.(error);
        }
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      if (requestId === requestIdRef.current) {
        setError(error);
        options?.onError?.(error);
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [query, JSON.stringify(variables), options?.skip]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(
    async (newVariables?: Record<string, any>) => {
      const requestId = ++requestIdRef.current;
      const vars = newVariables || variables;
      setLoading(true);
      setError(undefined);

      try {
        const response = await graphqlFetch<T>(query, vars);

        const storefrontOnlyError =
          Boolean(response.errors?.length) &&
          response.errors!.every((item) =>
            isStorefrontDisabledError(item.message),
          );

        if (response.data && (!response.errors?.length || storefrontOnlyError)) {
          if (requestId === requestIdRef.current) {
            setData(response.data);
            options?.onCompleted?.(response.data);
          }
        } else if (response.errors && response.errors.length > 0) {
          const error = new Error(response.errors[0].message);
          if (requestId === requestIdRef.current) {
            setError(error);
            options?.onError?.(error);
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        if (requestId === requestIdRef.current) {
          setError(error);
          options?.onError?.(error);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [query, variables, options]
  );

  return { data, loading, error, refetch };
}

// Hook for GraphQL mutations
export function useMutation<
  T = any,
  V extends Record<string, any> = Record<string, any>
>(
  mutation: string,
  options?: {
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const { addToast } = useBooking();

  const mutate = useCallback(
    async (variables?: V) => {
      setLoading(true);
      setError(undefined);

      try {
        const response = await graphqlFetch<T>(mutation, {
          ...(variables as Record<string, any>),
        });

        if (response.errors && response.errors.length > 0) {
          const error = new Error(response.errors[0].message);
          setError(error);
          options?.onError?.(error);
          addToast({
            message: checkoutErrorMessage(
              error.message,
              "Something went wrong. Please try again.",
            ),
            variant: "error",
          });
          return { data: undefined, error };
        } else if (response.data) {
          setData(response.data);
          options?.onCompleted?.(response.data);
          return { data: response.data, error: undefined };
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        setError(error);
        options?.onError?.(error);
        return { data: undefined, error };
      } finally {
        setLoading(false);
      }

      return { data: undefined, error: undefined };
    },
    [mutation, options]
  );

  return [mutate, { data, loading, error }] as const;
}

// Hook for lazy queries (manual execution)
export function useLazyQuery<T = any>(
  query: string,
  options?: {
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [called, setCalled] = useState(false);

  const execute = useCallback(
    async (variables?: Record<string, any>) => {
      setCalled(true);
      setLoading(true);
      setError(undefined);

      try {
        const response = await graphqlFetch<T>(query, variables);

        if (response.errors && response.errors.length > 0) {
          const error = new Error(response.errors[0].message);
          setError(error);
          options?.onError?.(error);
          return { data: undefined, error };
        } else if (response.data) {
          setData(response.data);
          options?.onCompleted?.(response.data);
          return { data: response.data, error: undefined };
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        setError(error);
        options?.onError?.(error);
        return { data: undefined, error };
      } finally {
        setLoading(false);
      }

      return { data: undefined, error: undefined };
    },
    [query, options]
  );

  return [execute, { data, loading, error, called }] as const;
}
